import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, Minus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import { Card, CardContent } from "@/components/ui/card";
import {
  useEquipmentContents,
  useAddEquipmentContent,
  useUpdateEquipmentContentQuantity,
  useUpdateEquipmentContentName,
  useRemoveEquipmentContent,
  type EquipmentContent,
} from "@/hooks/useEquipmentContents";
import { useAllEquipment } from "@/hooks/useEquipment";

interface EquipmentContentListProps {
  equipmentId: string;
}

/** Name of a free line; click to rename. */
function FreeContentName({
  content,
  onRename,
}: {
  content: EquipmentContent;
  onRename: (name: string) => void;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(content.name ?? "");

  if (!isEditing) {
    return (
      <button
        type="button"
        className="block max-w-full truncate text-left font-medium hover:underline"
        title="Click to rename"
        onClick={() => {
          setDraft(content.name ?? "");
          setIsEditing(true);
        }}
        data-testid={`equipment-content-name-${content.id}`}
      >
        {content.name}
      </button>
    );
  }

  const commit = () => {
    const name = draft.trim();
    if (name && name !== content.name) onRename(name);
    setIsEditing(false);
  };

  return (
    <Input
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          commit();
        } else if (e.key === "Escape") {
          e.preventDefault();
          setIsEditing(false);
        }
      }}
      className="h-8"
      aria-label="Content name"
      data-testid={`equipment-content-name-input-${content.id}`}
    />
  );
}

export const EquipmentContentList = ({
  equipmentId,
}: EquipmentContentListProps) => {
  const { data: contents = [], isLoading } = useEquipmentContents(equipmentId);
  const { data: allEquipment = [] } = useAllEquipment();
  const addContent = useAddEquipmentContent();
  const updateQuantity = useUpdateEquipmentContentQuantity();
  const updateName = useUpdateEquipmentContentName();
  const removeContent = useRemoveEquipmentContent();

  const [selectedEquipmentId, setSelectedEquipmentId] = useState<string>("");
  const [freeName, setFreeName] = useState("");

  // Filter out already added equipment and the current equipment itself
  const contentIds = new Set(contents.map((c) => c.child_equipment_id));
  const equipmentOptions = allEquipment
    .filter((e) => e.id !== equipmentId && !contentIds.has(e.id))
    .map((e) => ({
      value: e.id,
      label: e.name,
      subtitle: e.manufacturer || undefined,
    }));

  const handleAddStock = () => {
    if (selectedEquipmentId) {
      addContent.mutate(
        {
          parentEquipmentId: equipmentId,
          childEquipmentId: selectedEquipmentId,
        },
        { onSuccess: () => setSelectedEquipmentId("") },
      );
    }
  };

  const handleAddFree = (event: FormEvent) => {
    event.preventDefault();
    const name = freeName.trim();
    if (!name) return;
    addContent.mutate(
      { parentEquipmentId: equipmentId, name },
      { onSuccess: () => setFreeName("") },
    );
  };

  const handleQuantityChange = (contentId: string, quantity: number) => {
    if (quantity >= 1) {
      updateQuantity.mutate({
        id: contentId,
        parentEquipmentId: equipmentId,
        quantity,
      });
    }
  };

  const handleRemove = (contentId: string) => {
    if (confirm("Remove this item from contents?")) {
      removeContent.mutate({ id: contentId, parentEquipmentId: equipmentId });
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Loading contents...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Contents ({contents.length})</h3>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2">
          <Label>From stock</Label>
          <div className="flex gap-2">
            <div className="min-w-0 flex-1">
              <EntityCombobox
                options={equipmentOptions}
                value={selectedEquipmentId}
                onChange={(value) => setSelectedEquipmentId(value || "")}
                placeholder="Search equipment to add..."
                searchPlaceholder="Search by name..."
                testId="equipment-content-picker"
              />
            </div>
            <Button
              onClick={handleAddStock}
              disabled={!selectedEquipmentId || addContent.isPending}
              data-testid="add-equipment-content"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add
            </Button>
          </div>
        </div>
        <form className="space-y-2" onSubmit={handleAddFree}>
          <Label htmlFor="free-content-name">Free item</Label>
          <div className="flex gap-2">
            <Input
              id="free-content-name"
              value={freeName}
              onChange={(e) => setFreeName(e.target.value)}
              placeholder="e.g. Charger, power supply, case..."
              data-testid="free-equipment-content-input"
            />
            <Button
              type="submit"
              disabled={!freeName.trim() || addContent.isPending}
              data-testid="add-free-equipment-content"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add
            </Button>
          </div>
        </form>
      </div>

      {contents.length === 0 ? (
        <div className="text-center text-muted-foreground py-8 border rounded-lg">
          No contents added yet. Add equipment from stock or a free item above.
        </div>
      ) : (
        <div className="space-y-2">
          {contents.map((content) => {
            const child = content.child_equipment;
            return (
              <Card
                key={content.id}
                data-testid={`equipment-content-${content.id}`}
              >
                <CardContent className="p-3 md:p-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between md:gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="flex min-w-0 items-center gap-2">
                      {content.child_equipment_id ? (
                        <Link
                          to={`/equipment/${content.child_equipment_id}`}
                          className="truncate font-medium hover:underline"
                          data-testid={`equipment-content-name-${content.id}`}
                        >
                          {child?.name}
                        </Link>
                      ) : (
                        <FreeContentName
                          content={content}
                          onRename={(name) =>
                            updateName.mutate({
                              id: content.id,
                              parentEquipmentId: equipmentId,
                              name,
                            })
                          }
                        />
                      )}
                      <Badge
                        variant={
                          content.child_equipment_id ? "outline" : "secondary"
                        }
                        className="shrink-0 text-xs font-normal"
                      >
                        {content.child_equipment_id ? "Stock" : "Free"}
                      </Badge>
                    </div>
                    {child && (child.manufacturer || child.number) && (
                      <p className="text-sm text-muted-foreground truncate">
                        {child.manufacturer}
                        {child.manufacturer && child.number && " • "}
                        {child.number}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() =>
                        handleQuantityChange(content.id, content.quantity - 1)
                      }
                      disabled={content.quantity <= 1}
                      data-testid={`decrement-equipment-content-${content.id}`}
                    >
                      <Minus className="h-4 w-4" />
                    </Button>
                    <Input
                      type="number"
                      value={content.quantity}
                      onChange={(e) =>
                        handleQuantityChange(content.id, Number(e.target.value))
                      }
                      className="w-16 text-center"
                      min={1}
                      data-testid={`equipment-content-quantity-${content.id}`}
                    />
                    <Button
                      size="icon"
                      variant="outline"
                      onClick={() =>
                        handleQuantityChange(content.id, content.quantity + 1)
                      }
                      data-testid={`increment-equipment-content-${content.id}`}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => handleRemove(content.id)}
                      data-testid={`remove-equipment-content-${content.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
